import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeft, FileJson, ChevronDown, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, EmptyState, Spinner } from '../components/ui';
import SnapshotItemPreview from '../components/SnapshotItemPreview';
import { backupsApi } from '../lib/api';
import type { Snapshot, BackupItem } from '../types/vault';

export default function DataBrowser() {
  const { id } = useParams<{ id: string }>();
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [items, setItems] = useState<BackupItem[]>([]);
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedItemData, setSelectedItemData] = useState<unknown>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 50;

  useEffect(() => {
    if (id) loadSnapshot();
  }, [id]);

  // Reset to the first page when the filter changes
  useEffect(() => {
    setPage(0);
  }, [selectedType]);

  useEffect(() => {
    if (id) loadItems();
  }, [id, selectedType, page]);

  const loadSnapshot = async () => {
    try {
      const res = await backupsApi.getSnapshot(id!);
      if (res.success && res.data) {
        setSnapshot(res.data.snapshot);
        // No "All" view — always browse one resource type at a time
        if (res.data.snapshot.resourceTypes.length > 0) {
          setSelectedType((prev) => prev || res.data!.snapshot.resourceTypes[0]);
        }
      }
    } catch {
      setError('Failed to load snapshot');
    }
  };

  const loadItems = async () => {
    setLoading(true);
    setSelectedItemId(null);
    setSelectedItemData(null);
    try {
      const res = await backupsApi.browseItems(id!, selectedType || undefined, PAGE_SIZE, page * PAGE_SIZE);
      if (res.success && res.data) {
        setItems(res.data.items);
        setTotal(res.data.total ?? res.data.items.length);
      }
    } catch {
      setError('Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  const loadItemData = async (itemId: string) => {
    if (selectedItemId === itemId) {
      setSelectedItemId(null);
      setSelectedItemData(null);
      return;
    }
    setLoadingData(true);
    setSelectedItemId(itemId);
    try {
      const res = await backupsApi.getItemData(id!, itemId);
      if (res.success && res.data) setSelectedItemData(res.data.item);
    } catch {
      setError('Failed to load item data');
    } finally {
      setLoadingData(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/vault/snapshots" className="text-ink-3 hover:text-ink">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="ds-eyebrow">Vault</div>
            <h2 className="m-0 mt-1.5 font-display font-bold text-[24px] text-ink tracking-tight">Browse Snapshot</h2>
            {snapshot && (
              <p className="m-0 mt-1.5 font-medium text-[13px] text-ink-2 font-mono">
                {snapshot.connectionName || snapshot.platform} &middot; {snapshot.totalItems} items &middot; {new Date(snapshot.createdAt).toLocaleString()}
              </p>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-danger-soft border border-line text-danger px-4 py-3 rounded-[11px]">
            {error}
            <button onClick={() => setError(null)} className="ml-2 text-danger font-bold">&times;</button>
          </div>
        )}

        {/* Filter by resource type */}
        {snapshot && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-ink-2">Filter:</span>
            {snapshot.resourceTypes.map((type) => (
              <button
                key={type}
                onClick={() => setSelectedType(type)}
                className={`px-3 py-1 text-sm rounded-full font-mono transition-colors ${
                  selectedType === type ? 'bg-accent text-white' : 'bg-surface-2 text-ink-2 hover:bg-surface'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <EmptyState icon={FileJson} title="No items found" />
        ) : (
          <Card className="divide-y divide-line-2 overflow-hidden">
            {items.map((item) => (
              <div key={item.id}>
                <button
                  onClick={() => loadItemData(item.id)}
                  className="w-full flex items-center justify-between px-6 py-3 hover:bg-surface-2 text-left transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {selectedItemId === item.id ? (
                      <ChevronDown className="w-4 h-4 text-ink-3" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-ink-3" />
                    )}
                    <div>
                      <span className="text-sm font-medium text-ink">{item.resourceName || item.resourceId}</span>
                      <span className="text-xs text-ink-3 ml-2 font-mono">#{item.resourceId}</span>
                    </div>
                  </div>
                  <span className="text-xs text-ink-3 font-mono">{item.sizeBytes} bytes</span>
                </button>
                {selectedItemId === item.id && (
                  <div className="px-6 pb-4">
                    {loadingData ? (
                      <div className="flex justify-center py-4">
                        <Spinner className="!h-5 !w-5" />
                      </div>
                    ) : (
                      <div className="bg-surface border border-line rounded-[11px] p-5">
                        <SnapshotItemPreview
                          resourceType={item.resourceType}
                          data={selectedItemData}
                          filename={`${item.resourceType}-${(item.resourceName || item.resourceId).replace(/[^\w.-]+/g, '_')}.json`}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </Card>
        )}

        {/* Pagination */}
        {!loading && total > PAGE_SIZE && (
          <div className="flex items-center justify-between">
            <span className="text-[13px] text-ink-3 font-mono">
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total} items
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3.5 py-1.5 text-sm font-semibold rounded-[9px] border border-line bg-surface text-ink-2 hover:border-accent disabled:opacity-40 disabled:hover:border-line"
              >
                ← Previous
              </button>
              <span className="text-[13px] text-ink-2 font-mono px-1">
                {page + 1} / {Math.ceil(total / PAGE_SIZE)}
              </span>
              <button
                onClick={() => setPage((p) => ((p + 1) * PAGE_SIZE < total ? p + 1 : p))}
                disabled={(page + 1) * PAGE_SIZE >= total}
                className="px-3.5 py-1.5 text-sm font-semibold rounded-[9px] border border-line bg-surface text-ink-2 hover:border-accent disabled:opacity-40 disabled:hover:border-line"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
